import {CreateEventData} from 'organization/Event/CreateEventData'
import {Organization} from 'organization/models/Organization'
import {organizationApiClient} from 'organization/organizationApiClient'
import {FullPageLoader} from 'organization/theme/FullPageLoader'
import React, {useEffect, useState} from 'react'
import {toast} from 'react-toastify'
import {useRouteOrganizationId} from 'utils/lib/url'

export interface OrganizationContextProps {
  organization: Organization | null
  createEvent: (data: CreateEventData) => Promise<any>
  updateCompany: (
    data: UpdateOrganizationCompanyData,
  ) => Promise<string | boolean>
  hasCompany: () => boolean
}

export const OrganizationContext = React.createContext<
  OrganizationContextProps | undefined
>(undefined)

export default function OrganizationProvider(props: {
  children: React.ReactElement
}) {
  const apiClient = organizationApiClient()
  const [organization, setOrganization] = useState<Organization | null>(null)
  const [loading, setLoading] = useState<boolean>(true)

  const organizationId = useRouteOrganizationId()

  useEffect(() => {
    get()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const get = async () => {
    try {
      const response = await apiClient.get(`api/organization/${organizationId}`)

      setOrganization(response)
      setLoading(false)
    } catch (error: any) {
      toast.warning(error.response.data.message)
      setLoading(false)
    }
  }

  const createEvent = async (data: CreateEventData) => {
    if (loading) return
    setLoading(true)
    const result = await apiClient
      .post(`api/organization/${organizationId}/event/create`, data)
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })
      .finally(() => setLoading(false))

    if (result) return result

    return false
  }

  const updateCompany = async (data: UpdateOrganizationCompanyData) => {
    const result = await apiClient
      .post(`api/organization/${organizationId}/update-company`, data)
      .then(get)
      .then(() => {
        return true
      })
      .catch((error) => {
        toast.warning(error.response.data.message)
        return false
      })

    return result
  }

  const hasCompany = (): boolean => {
    return (
      !!organization?.eik &&
      organization?.eik.trim() !== '' &&
      !!organization?.company_name &&
      organization?.company_name.trim() !== '' &&
      !!organization?.mol &&
      organization?.mol.trim() !== '' &&
      !!organization?.country &&
      organization?.country.trim() !== '' &&
      !!organization?.city &&
      organization?.city.trim() !== '' &&
      !!organization?.postal_code &&
      organization?.postal_code.trim() !== '' &&
      !!organization?.address_line &&
      organization?.address_line.trim() !== ''
    )
  }

  return (
    <OrganizationContext.Provider
      value={{
        organization,
        createEvent,
        updateCompany,
        hasCompany,
      }}
    >
      {loading && <FullPageLoader />}
      {!loading && props.children}
    </OrganizationContext.Provider>
  )
}

export function useOrganization() {
  const context = React.useContext(OrganizationContext)
  if (context === undefined) {
    throw new Error('useOrganization must be used within OrganizationProvider')
  }
  return context
}

export type UpdateOrganizationCompanyData = Pick<
  Organization,
  | 'company_name'
  | 'eik'
  | 'vat_number'
  | 'mol'
  | 'country'
  | 'city'
  | 'postal_code'
  | 'address_line'
>
